import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { signOut } from '../../firebase/auth';
import Button from '../ui/Button';
import Logo from '../shared/Logo';

/**
 * Navbar - Modern Sleek Design System
 *
 * Elevated dark navbar with glass morphism effect,
 * glowing accent line, and refined interactions.
 */
export default function Navbar() {
  const { user, userProfile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Get photo URL from profile (Firestore) or user (Auth)
  const photoURL = userProfile?.photoURL || user?.photoURL;

  // Close menu when clicking outside (desktop dropdown only)
  useEffect(() => {
    function handleClickOutside(event) {
      // Don't close if clicking the mobile menu button or inside mobile menu
      if (mobileButtonRef.current?.contains(event.target)) {
        return;
      }
      if (mobileMenuRef.current?.contains(event.target)) {
        return;
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-sticky self-start w-full">
      {/* Glass background with gradient border */}
      <div className="absolute inset-0 bg-midnight/80 backdrop-blur-md" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="page-container relative">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/">
            <Logo size="md" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/browse"
              className="text-cream/70 hover:text-cream transition-colors text-sm font-medium"
            >
              Browse
            </Link>
            <Link
              to="/how-it-works"
              className="text-cream/70 hover:text-cream transition-colors text-sm font-medium"
            >
              How It Works
            </Link>
            <Link to="/create">
              <Button variant="accent" size="sm">
                Sell
              </Button>
            </Link>

            {isAuthenticated ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className={`
                    flex items-center gap-2 px-2 py-1.5 rounded-full
                    transition-all duration-200
                    hover:bg-midnight-lighter
                    ${menuOpen ? 'bg-midnight-lighter ring-2 ring-primary/30' : ''}
                  `}
                  aria-label="User menu"
                  aria-expanded={menuOpen}
                >
                  {/* Avatar */}
                  <div className={`
                    w-8 h-8 rounded-full overflow-hidden
                    ring-2 transition-all duration-200
                    ${menuOpen ? 'ring-primary' : 'ring-primary/40 hover:ring-primary/70'}
                  `}>
                    {photoURL ? (
                      <img
                        src={photoURL}
                        alt={user?.displayName || 'User'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary to-petrol flex items-center justify-center">
                        <span className="text-cream text-sm font-bold">
                          {user?.displayName?.[0]?.toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Dropdown chevron */}
                  <svg
                    className={`w-4 h-4 text-cream/60 transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-midnight-light/95 backdrop-blur-sm rounded-xl shadow-xl border border-primary/20 py-2 z-dropdown animate-fade-in">
                    {/* User info header */}
                    <div className="px-4 py-2 border-b border-cream/10 mb-1">
                      <p className="text-cream font-medium truncate">{user?.displayName || 'User'}</p>
                      <p className="text-cream/50 text-sm truncate">{user?.email}</p>
                    </div>

                    <Link
                      to="/dashboard"
                      className="flex items-center gap-3 px-4 py-2.5 text-cream/70 hover:text-cream hover:bg-primary/10 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      <DashboardIcon className="w-4 h-4" />
                      Dashboard
                    </Link>
                    <Link
                      to="/my-entries"
                      className="flex items-center gap-3 px-4 py-2.5 text-cream/70 hover:text-cream hover:bg-primary/10 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      <TicketIcon className="w-4 h-4" />
                      My Entries
                    </Link>
                    <Link
                      to="/my-competitions"
                      className="flex items-center gap-3 px-4 py-2.5 text-cream/70 hover:text-cream hover:bg-primary/10 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      <TrophyIcon className="w-4 h-4" />
                      My Competitions
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2.5 text-cream/70 hover:text-cream hover:bg-primary/10 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      <SettingsIcon className="w-4 h-4" />
                      Settings
                    </Link>

                    <hr className="my-2 border-cream/10" />

                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        handleSignOut();
                      }}
                      className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-cream/70 hover:text-error hover:bg-error/10 transition-colors"
                    >
                      <SignOutIcon className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/auth/login">
                  <Button variant="ghost">Log In</Button>
                </Link>
                <Link to="/auth/signup">
                  <Button variant="primary">Sign Up</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            ref={mobileButtonRef}
            className="md:hidden p-2 text-cream/70 hover:text-cream transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div ref={mobileMenuRef} className="md:hidden border-t border-midnight-lighter py-4 animate-fade-in">
            <div className="flex flex-col gap-3">
              {isAuthenticated ? (
                <>
                  {/* Mobile user info - first */}
                  <div className="flex items-center gap-3 pb-4 border-b border-cream/10">
                    <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-primary/40">
                      {photoURL ? (
                        <img src={photoURL} alt={user?.displayName || 'User'} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary to-petrol flex items-center justify-center">
                          <span className="text-cream text-lg font-bold">{user?.displayName?.[0]?.toUpperCase() || 'U'}</span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-cream font-semibold truncate">{user?.displayName || 'User'}</p>
                      <p className="text-cream/50 text-sm truncate">{user?.email}</p>
                    </div>
                  </div>

                  {/* Account links */}
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <DashboardIcon className="w-5 h-5" />
                    Dashboard
                  </Link>
                  <Link
                    to="/my-entries"
                    className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <TicketIcon className="w-5 h-5" />
                    My Entries
                  </Link>
                  <Link
                    to="/my-competitions"
                    className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <TrophyIcon className="w-5 h-5" />
                    My Competitions
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <SettingsIcon className="w-5 h-5" />
                    Settings
                  </Link>

                  {/* Divider */}
                  <div className="border-t border-cream/10 my-2" />

                  {/* Navigation links */}
                  <Link
                    to="/browse"
                    className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <BrowseIcon className="w-5 h-5" />
                    Browse Competitions
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="flex items-center gap-3 text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <InfoIcon className="w-5 h-5" />
                    How It Works
                  </Link>
                  <Link
                    to="/create"
                    className="flex items-center gap-3 text-accent hover:text-accent/80 transition-colors font-semibold py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    <PlusIcon className="w-5 h-5" />
                    Sell an Item
                  </Link>

                  {/* Divider */}
                  <div className="border-t border-cream/10 my-2" />

                  {/* Sign out */}
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      handleSignOut();
                    }}
                    className="flex items-center gap-3 text-left text-cream/60 hover:text-error transition-colors font-medium py-1"
                  >
                    <SignOutIcon className="w-5 h-5" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  {/* Navigation links for guests */}
                  <Link
                    to="/browse"
                    className="text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    Browse Competitions
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="text-cream/80 hover:text-cream transition-colors font-medium py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    How It Works
                  </Link>
                  <Link
                    to="/create"
                    className="text-accent font-semibold py-1"
                    onClick={() => setMenuOpen(false)}
                  >
                    Sell an Item
                  </Link>

                  <div className="flex flex-col gap-2 pt-4 border-t border-cream/10">
                    <Link to="/auth/login" onClick={() => setMenuOpen(false)}>
                      <Button variant="outline" className="w-full">Log In</Button>
                    </Link>
                    <Link to="/auth/signup" onClick={() => setMenuOpen(false)}>
                      <Button variant="primary" className="w-full">Sign Up</Button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Icons for dropdown menu
function DashboardIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  );
}

function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );
}

function TrophyIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3h14M5 3a2 2 0 00-2 2v1a3 3 0 003 3h1m11-6a2 2 0 012 2v1a3 3 0 01-3 3h-1m-5 0v6m-3 0h6m-3-6a3 3 0 01-3-3V5a2 2 0 012-2h2a2 2 0 012 2v4a3 3 0 01-3 3z" />
    </svg>
  );
}

function SettingsIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function SignOutIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function BrowseIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function InfoIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  );
}
